import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';

import { CommonModule } from '@angular/common';
import { ReplaySubject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { BaseContainer, ShUrlQueryService } from '../../../../core/public_api';
import { ShActionItemComponent } from '../../../../elements/public_api';

import { isEqual } from 'lodash-es';
import { ShFormMultiSelectModule } from '../../../../form-controls/components/form-multi-select/form-multi-select.module';
import {
    FormTagListActionItem,
    FormTagListActionItemLocation
} from '../../../../form-controls/components/form-tag-list/main/form-tag-list-action-item.class';
import { MultiFormSelectOptions } from '../../../../form-controls/entities/multi-form-select-options.entity';
import { QueryPartData } from '../../base/entities/query-part-data.entity';
import { QueryPartStrategy } from '../../base/entities/query-part-strategy';

@Component({
    standalone: true,
    selector: 'sh-field-search',
    templateUrl: './field-search.component.html',
    styleUrls: ['field-search.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ReactiveFormsModule, ShActionItemComponent, ShFormMultiSelectModule]
})
export class ShFieldSearchComponent extends BaseContainer implements OnInit {
    private readonly urlQueryService: ShUrlQueryService = inject(ShUrlQueryService);

    public actionItem: FormTagListActionItem;
    public fieldControl: UntypedFormControl = new UntypedFormControl();
    public searchControl: UntypedFormControl = new UntypedFormControl('');
    public fieldSearchOptions$: ReplaySubject<MultiFormSelectOptions> = new ReplaySubject<MultiFormSelectOptions>(1);

    @Input() prefix: string;
    @Input() placeholder: string;

    @Input() set fieldSearchOptions(fieldSearchOptions: MultiFormSelectOptions) {
        if (fieldSearchOptions) {
            this.fieldSearchOptions$.next(fieldSearchOptions);
            this.initValueSync(fieldSearchOptions);
        }
    }

    public ngOnInit(): void {
        this.initActionItem();
        this.initFieldChangeListener();
    }

    private initValueSync(fieldSearchOptions: MultiFormSelectOptions) {
        this.urlQueryService
            .getFieldsQuery()
            .pipe(distinctUntilChanged(isEqual), takeUntil(this.destroy$))
            .subscribe((queryFields: QueryPartData[]) => {
                const supportedFieldKeys = fieldSearchOptions.options.map(option => option.value);

                const supportedFields = queryFields.filter(field => supportedFieldKeys.includes(field.field));

                if (supportedFields.length > 0) {
                    this.fieldControl.setValue([supportedFields[0].field], { emitEvent: false });
                    this.searchControl.setValue(supportedFields[0].values[0] ?? '', { emitEvent: false });
                } else {
                    this.setDefaultOption(fieldSearchOptions);
                }
            });
    }

    private initActionItem(): void {
        this.actionItem = new FormTagListActionItem(FormTagListActionItemLocation.Joined, 'search');
        this.actionItem.eventListener = () => this.onSearchButtonClicked();
    }

    private initFieldChangeListener(): void {
        this.fieldControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.onSearchButtonClicked());
    }

    private setDefaultOption(fieldSearchOptions: MultiFormSelectOptions): void {
        this.fieldControl.setValue([fieldSearchOptions.options[0].value], { emitEvent: false });
    }

    private onSearchButtonClicked(): void {
        // Clear query if empty
        const queryParts = this.searchControl.value
            ? [
                  {
                      strategy: QueryPartStrategy.MatchAll,
                      field: this.fieldControl.value[0],
                      values: [this.searchControl.value]
                  }
              ]
            : [];

        this.urlQueryService.setFieldsQuery(queryParts);
    }

    public onSearchKeyUp(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            this.onSearchButtonClicked();
        }
    }
}
